/**
 * End-of-lesson recap bookkeeping. When a lesson (or its practice questions) starts, the child's lifetime Sparks,
 * balance and Sparks Stars board are noted; the recap compares them with the live values. The note lives in
 * sessionStorage per child, so it survives the lesson-to-quiz navigation and a refresh but not a closed tab. The
 * server stays the source of truth for every amount: losing the note only hides the "earned" line.
 */
export const TALLY_MAX_AGE_MS = 3 * 60 * 60 * 1000;

const key = (childId) => `lessonTally:${childId}`;

const storageOf = (storage) => storage || (typeof sessionStorage !== 'undefined' ? sessionStorage : null);

function read(childId, storage) {
  try {
    const raw = storageOf(storage)?.getItem(key(childId));
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

function write(childId, tally, storage) {
  try {
    storageOf(storage)?.setItem(key(childId), JSON.stringify(tally));
  } catch (e) {
    // Private mode / blocked storage: the recap simply shows no "earned" line.
  }
}

/** The open tally for this child, or null when there is none, it was shown already or it is from hours ago. */
export function readTally(childId, { storage, now = Date.now() } = {}) {
  const t = childId ? read(childId, storage) : null;
  return t && !t.closed && now - t.at < TALLY_MAX_AGE_MS ? t : null;
}

/** Opens a tally unless one is already open (the quiz after a lesson keeps the lesson's). Returns the new one or null. */
export function beginTally(childId, { lifetime, balance }, { storage, now = Date.now() } = {}) {
  if (!childId || readTally(childId, { storage, now })) return null;
  const tally = { at: now, lifetime, balance, board: null };
  write(childId, tally, storage);
  return tally;
}

/** Notes the starting board (a boardSnapshot) on the tally opened at `at`, if it is still the open one. */
export function setTallyBoard(childId, at, board, storage) {
  const t = read(childId, storage);
  if (t && t.at === at && !t.closed) write(childId, { ...t, board }, storage);
}

/** The recap has been shown: the next lesson starts a fresh tally. */
export function closeTally(childId, storage) {
  const t = childId ? read(childId, storage) : null;
  if (t && !t.closed) write(childId, { ...t, closed: true }, storage);
}

/**
 * The child's weekly place (1-based), 0 when they have not earned this week, or null when the board is off.
 * Opted-out children still see their own place; others see them as "Someone".
 */
export function rankOfYou(board) {
  if (!board?.enabled) return null;
  if (typeof board.you?.rank === 'number') return board.you.rank;
  return (board.top || []).findIndex((row) => row.isYou) + 1;
}

/** Rows the climb animation draws: neighbourhood when you are in it, otherwise the top list with absolute ranks. */
export function climbSourceRows(board) {
  const hood = board.neighbourhood || [];
  if (hood.some((r) => r.isYou)) {
    return hood.map(({ firstName, sparks, equipped, isYou, rank }) => ({ firstName, sparks, equipped, isYou, rank }));
  }
  return (board.top || []).map((r, i) => ({
    firstName: r.firstName,
    sparks: r.sparks,
    equipped: r.equipped,
    isYou: r.isYou,
    rank: r.rank ?? i + 1,
  }));
}

/** What the recap keeps of a GET leaderboard reply, or null when the board is off. */
export function boardSnapshot(board) {
  const rank = rankOfYou(board);
  if (rank == null) return null;
  return {
    week: board.weekKeyUK,
    rank,
    sparks: board.you?.sparks ?? 0,
    gap: board.you?.gapToTop ?? 0,
    gapToNext: board.you?.gapToNext ?? 0,
    top: climbSourceRows(board),
  };
}

/**
 * The Sparks Stars news since the lesson started, or null. Only good news, within the same week:
 * - `climb`: up the board (top 10 or neighbourhood), from place `from` to `to`;
 * - `entered`: from no place this week to place `to`;
 * - `closer`: same place but fewer Sparks to the person above (or to the top 10 when not on the board yet).
 * `rows` is the end board slice the climb animation draws (each row has an absolute `rank`).
 */
export function boardClimb(start, endBoard) {
  const end = boardSnapshot(endBoard);
  if (!start || !end || start.week !== end.week) return null;
  if (end.rank > 0 && start.rank > end.rank) {
    const up = start.rank - end.rank;
    return { kind: 'climb', from: start.rank, to: end.rank, rows: end.top, text: `You moved up ${up} ${up === 1 ? 'place' : 'places'} to number ${end.rank}!` };
  }
  if (end.rank > 0 && start.rank === 0) {
    return { kind: 'entered', from: null, to: end.rank, rows: end.top, text: `You made it onto Sparks Stars at number ${end.rank}!` };
  }
  if (end.rank > 0 && start.rank === end.rank && (end.gapToNext ?? 0) < (start.gapToNext ?? 0)) {
    const by = start.gapToNext - end.gapToNext;
    const span = end.sparks + end.gapToNext;
    const share = (s) => {
      const g = s.gapToNext ?? s.gap ?? 0;
      return s.sparks + g > 0 ? s.sparks / (s.sparks + g) : 0;
    };
    return {
      kind: 'closer',
      by,
      gap: end.gapToNext,
      fromShare: share(start),
      toShare: share(end),
      text: `You're ${by.toLocaleString('en-GB')} Sparks closer to the person above you!`,
      label: span > 0 ? 'Road to the next place' : undefined,
    };
  }
  if (end.rank === 0 && start.rank === 0 && end.gap < start.gap) {
    const share = (s) => (s.sparks + s.gap > 0 ? s.sparks / (s.sparks + s.gap) : 0);
    return { kind: 'closer', by: start.gap - end.gap, gap: end.gap, fromShare: share(start), toShare: share(end), text: `You're ${(start.gap - end.gap).toLocaleString('en-GB')} Sparks closer to the top 10!` };
  }
  return null;
}
